import { Svg, G, Point, Container, Matrix, Rect,
         Element as SvgElement
       } from '@svgdotjs/svg.js';
import { StageConstants } from 'core/constants';
import { Utils } from 'core/utils';

import { ClickInfo } from '../image-cropper/drag-sensor';
import { Handle, HandleBehavior, Placement, Position } from './handle';
import { MoveBehavior, RotationBehavior, ScaleBehavior
       } from './handle.behaviors';

export interface TransformationConstraints {
  minWidth: number;
  minHeight: number;
}

export interface PlacementConstraint {
  isSatisfied(): boolean;
  slideIntoAllowedArea(): void;
  pullWidthIntoAllowedArea(): void;
}

interface PlacementSnapshot {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

const APPROACH_STEPS = 10;
const MOVING_BORDER_SCREEN_WIDTH = 8;

interface Span {
  start: number;
  size: number;
}

function clampedSpan(
  start: number | undefined,
  size: number | undefined,
  minSize: number,
  current: Span): Span
{
  if (size === undefined) {
    return {
      start: start === undefined ? current.start : start,
      size: current.size,
    };
  }

  const clampedSize = Math.max(size, minSize);

  return {
    start: start === undefined
      ? current.start
      : start + (size - clampedSize) / 2,
    size: clampedSize,
  };
}

function interpolated(
  from: PlacementSnapshot,
  to: PlacementSnapshot,
  fraction: number): PlacementSnapshot
{
  const between = (a: number, b: number): number => a + (b - a) * fraction;
  const rotationDelta = ((to.rotation - from.rotation + 540) % 360) - 180;

  return {
    x: between(from.x, to.x),
    y: between(from.y, to.y),
    width: between(from.width, to.width),
    height: between(from.height, to.height),
    rotation: from.rotation + rotationDelta * fraction,
  };
}

export class TransformHandle {
  private movingControl: Rect;
  private movingBorder: Rect;
  private scaleControls: { left: Rect; right: Rect };
  private moveHandle: Handle;
  private handles: Array<Handle>;
  private rotationHandle: Handle;
  private group: G;
  private handleGroup: G;
  private doc: Svg;

  constructor(
    private element: SvgElement,
    private constraintsOf: () => TransformationConstraints,
    private onUpdateHandler: () => void,
    private onDragEndHandler: () => void,
    onClickHandler: (click: ClickInfo) => void,
    private constraint?: PlacementConstraint)
  {
    this.doc = (<any>element).root() as Svg;
    this.group = ensureRotationGroup(element);
    this.handleGroup = this.doc.group()
      .addClass(StageConstants.GROUP_TAG_TRANSFORM_HANDLE);

    this.group.front();
    this.handleGroup.front();

    this.movingControl = this.handleGroup.rect().css({
      'fill': 'transparent',
      'cursor': 'grab',
      'pointer-events': 'auto',
    });
    this.movingBorder = this.handleGroup.rect()
      .attr({
        'fill': 'none',
        'stroke': 'transparent',
        'stroke-width': MOVING_BORDER_SCREEN_WIDTH,
        'vector-effect': 'non-scaling-stroke',
        'pointer-events': 'stroke',
      })
      .css('cursor', 'grab');
    this.scaleControls = {
      left: this.createScaleControl(),
      right: this.createScaleControl(),
    };

    const stageHandleWith = (
        behavior: HandleBehavior, control: SvgElement): Handle => {
      const handle = new Handle(element, behavior);
      handle.applyControl(control);
      handle.onClick = onClickHandler;
      return handle;
    };
    this.moveHandle = stageHandleWith(new MoveBehavior(), this.movingControl);
    this.rotationHandle = new Handle(element, new RotationBehavior());

    this.handles = [
      this.moveHandle,
      stageHandleWith(new MoveBehavior(), this.movingBorder),
      stageHandleWith(new ScaleBehavior(-1), this.scaleControls.left),
      stageHandleWith(new ScaleBehavior(1), this.scaleControls.right),
      this.rotationHandle,
    ];
    for (const handle of this.handles) {
      handle.onRequestUpdate = this.update.bind(this);
      handle.onDragEnd = () => this.onDragEndHandler();
    }

    this.setPlace({});
    this.refresh();
  }

  private createScaleControl(): Rect
  {
    const viewbox = this.doc.viewbox();
    const longSide = Math.max(viewbox.width, viewbox.height) *
        (Utils.isLargeDevice() ? 1 : 2);
    const width = longSide / 115;

    return this.handleGroup.rect(width, longSide / 30)
      .radius(width / 2)
      .fill('white')
      .stroke({ width: longSide / 500 })
      .addClass('handle-scale-control')
      .css('cursor', 'ew-resize');
  }

  public teardown(): void
  {
    for (const handle of this.handles) {
      handle.teardown();
    }
    this.handleGroup.remove();
  }

  public applyMovingEnabled(enabled: boolean): void
  {
    this.moveHandle.isEnabled = enabled;
    this.movingControl.css('cursor', enabled ? 'grab' : 'text');
  }

  public get movingControlNode(): Element
  {
    return this.movingControl.node;
  }

  public attachRotationControl(control: HTMLElement | undefined): void
  {
    this.rotationHandle.applyControl(control);
  }

  private update(place: Placement): void
  {
    const wasSatisfied = !this.constraint || this.constraint.isSatisfied();
    const previous = this.placementSnapshot();

    this.setPlace(place);
    if (this.constraint && wasSatisfied && place.width !== undefined) {
      this.constraint.pullWidthIntoAllowedArea();
    }
    this.onUpdateHandler();

    if (this.constraint && wasSatisfied &&
        !this.constraint.isSatisfied()) {
      const isTranslation = place.width === undefined &&
          place.height === undefined &&
          place.rotation === undefined;

      if (isTranslation) {
        this.constraint.slideIntoAllowedArea();
      }
      else {
        this.approachPlacement(previous, this.placementSnapshot());
      }
      this.onUpdateHandler();
    }

    this.refresh();
  }

  private approachPlacement(
    from: PlacementSnapshot, to: PlacementSnapshot): void
  {
    let satisfied = 0;
    let unsatisfied = 1;

    for (let step = 0; step < APPROACH_STEPS; step++) {
      const fraction = (satisfied + unsatisfied) / 2;
      this.applyPlacementSnapshot(interpolated(from, to, fraction));
      this.onUpdateHandler();

      if (this.constraint.isSatisfied()) {
        satisfied = fraction;
      }
      else {
        unsatisfied = fraction;
      }
    }

    this.applyPlacementSnapshot(interpolated(from, to, satisfied));
  }

  private placementSnapshot(): PlacementSnapshot
  {
    return {
      x: this.element.x() as number,
      y: this.element.y() as number,
      width: this.element.width() as number,
      height: this.element.height() as number,
      rotation: <any>this.group.transform('rotate'),
    };
  }

  private applyPlacementSnapshot(snapshot: PlacementSnapshot): void
  {
    this.element.x(snapshot.x);
    this.element.y(snapshot.y);
    this.element.width(snapshot.width);
    this.element.height(snapshot.height);
    this.rotateGroupTo(snapshot.rotation);
  }

  private rotateGroupTo(rotation: number): void
  {
    const rotationDelta = rotation - <any>this.group.transform('rotate');
    if (rotationDelta !== 0) {
      this.group.rotate(
          rotationDelta, this.element.cx(), this.element.cy());
    }
  }

  private get groupToDoc(): Matrix
  {
    return this.doc.screenCTM().inverse().multiply(this.group.screenCTM());
  }

  private setPlace(place: Placement): void
  {
    const constraints = this.constraintsOf();
    const horizontal = clampedSpan(place.x, place.width, constraints.minWidth, {
      start: this.element.x() as number,
      size: this.element.width() as number,
    });
    const vertical = clampedSpan(place.y, place.height, constraints.minHeight, {
      start: this.element.y() as number,
      size: this.element.height() as number,
    });

    const newCenter = (new Point(
      horizontal.start + horizontal.size / 2,
      vertical.start + vertical.size / 2
    ).transform(this.groupToDoc) as any) as Position;

    const bounds = this.doc.viewbox();

    if (newCenter.x < bounds.x ||
        newCenter.y < bounds.y ||
        newCenter.x > bounds.x + bounds.width ||
        newCenter.y > bounds.y + bounds.height)
    {
      return;
    }

    this.element.x(horizontal.start);
    this.element.y(vertical.start);
    this.element.width(horizontal.size);
    this.element.height(vertical.size);

    if (place.rotation !== undefined) {
      this.rotateGroupTo(place.rotation);
    }
  }

  public refresh(): void
  {
    this.alignHandleGroup();

    const x = this.element.x() as number;
    const y = this.element.y() as number;
    const width = this.element.width() as number;
    const height = this.element.height() as number;

    const handleGroupToScreen =
        (this.handleGroup.node as SVGGraphicsElement).getScreenCTM();
    const unitsPerScreenPixel =
        1 / Math.hypot(handleGroupToScreen.a, handleGroupToScreen.b);
    const borderOffset = MOVING_BORDER_SCREEN_WIDTH / 2 * unitsPerScreenPixel;
    const markerLineOutset =
        StageConstants.TEXT_MARKER_BORDER_SCREEN_WIDTH / 2 *
        unitsPerScreenPixel;

    this.movingControl.move(x, y).size(width, height);
    this.movingBorder
      .move(x - borderOffset, y - borderOffset)
      .size(width + 2 * borderOffset, height + 2 * borderOffset);
    this.scaleControls.left.center(x - markerLineOutset, y + height / 2);
    this.scaleControls.right.center(
        x + width + markerLineOutset, y + height / 2);
  }

  private alignHandleGroup(): void
  {
    this.handleGroup.transform(this.groupToDoc);
  }
}

function ensureRotationGroup(element: SvgElement): G {
  const parent = (<any>element).parent() as Container;

  if (parent.node.tagName !== 'g') {
    const wrapper = parent.group();
    element.putIn(wrapper);
    return wrapper;
  } else {
    return parent as G;
  }
}
